/**
 * useAuth.js — Authentication hook
 *
 * Manages the full auth lifecycle via the Express backend:
 * - Google Sign-In via /api/auth/google (JWT in httpOnly cookie) — the ONLY
 *   auth path as of 2026-07-28; password login/signup/change were removed and
 *   their routes now return 410.
 * - Profile loading via /api/auth/me
 * - Logout
 *
 * Provides an AuthContext so any component can access the current user,
 * their profile, and auth actions via useAuth().
 */

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { logAction } from '../lib/actionLogger';
import { queryClient } from '../lib/queryClient';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [profile, setProfile] = useState(null);        // profiles table row
  const [loading, setLoading] = useState(true);        // Initial load state
  const [authError, setAuthError] = useState(null);    // Last auth error

  // ── Initialize: try to restore session via cookie ───────────────────────
  useEffect(() => {
    let mounted = true;
    api.get('/auth/me')
      .then((data) => {
        if (mounted) setProfile(data);
      })
      .catch(() => {
        // Not authenticated — that's fine
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => { mounted = false; };
  }, []);

  // ── Listen for role_changed events from api.js ──────────────────────────
  useEffect(() => {
    const handler = async () => {
      try { await api.post('/auth/logout'); } catch {}
      queryClient.clear();
      setProfile(null);
      setAuthError('Your role was updated. Please log in again.');
    };
    window.addEventListener('rfos:role-changed', handler);
    return () => window.removeEventListener('rfos:role-changed', handler);
  }, []);

  // ── Logout ───────────────────────────────────────────────────────────────
  const logout = useCallback(async () => {
    logAction({ action: 'Logout' });
    try {
      await api.post('/auth/logout');
    } catch (e) {
      console.error('Logout error:', e.message);
    }

    queryClient.clear();
    setProfile(null);
  }, []);

  // ── Google Login ─────────────────────────────────────────────────────────
  const loginWithGoogle = useCallback(async (credential) => {
    setAuthError(null);
    try {
      const data = await api.post('/auth/google', { credential });
      setProfile(data.user);
      logAction({ action: 'Login (Google)', user: data.user.email });
      return { data };
    } catch (err) {
      setAuthError(err.message);
      return { error: err };
    }
  }, []);

  // ── Refresh Profile ──────────────────────────────────────────────────────
  const refreshProfile = useCallback(async () => {
    try {
      const data = await api.get('/auth/me');
      setProfile(data);
    } catch {
      // ignore
    }
  }, []);

  const value = {
    // State
    user: profile,
    profile,
    loading,
    authError,
    isAuthenticated: !!profile,

    // Actions
    loginWithGoogle,
    logout,
    refreshProfile,
    clearError: () => setAuthError(null),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to access auth state and actions.
 * Must be used within <AuthProvider>.
 */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
